import { Media } from '../models/media';
import { Coords } from './coords';
import { LanguageOption, LanguageMap } from './language';
import { DatabaseModel } from './database-model';
import { BillingType, PlanType } from './subscription-plan';
import { PartnerCostTransferType, PartnerCostType } from './product';


export class Project extends DatabaseModel {
    public key: string = "";
    public title: string = "";
    public text: string = "";
    public published: boolean = false;
    public order: number = 1;
    public image: Media = new Media();
    public creatorPlan: PlanType = PlanType.PERSONAL;

    // Firebase time stamp for plan creation/upgrade/downgrade
    public planChangedOn: any = "";
    public deletedOn: Date = null;
    // Billing type (null for Basic Plan)
    public billingType: BillingType = null;

    // Languages supported in project
    public languages: string;

    // Owner denoted by userkey
    public owner: string = "";
    // Members (owner + collaborators) denoted by {[userkey: string]: boolean}
    // We will eventually change the naming to 'members'
    // The boolean is a remain of an old setup and no longer has meaning
    public owners: any = {};
    // Firebase time stamp object on or timestamp string
    public createdOn: any = "";
    public position: Coords = new Coords(0, 0, false);

    public mapStyle: string = 'default';
    // Allow user to select offline mode of this world (and show option in welcome)
    public allowOfflineMode: boolean = false;
    // Exclusive route view (only show one route/collection at a time on the map
    public exclusiveRouteMode: boolean = false;

    // Active (currently selected) tour key of project (dynamically populated)
    public activeRouteKey: string;
    // Active preview key of active tour of project (dynamically populated)
    public activePreviewKey: string;
    // Percentage of tour product price transferred to partner on tour purchase
    public partnerPct: number = 0;
    // Partner ID refers to the Stripe connect account that will receive money when tours within world are purchased
    public partnerId: string = "";
    // Partner cost transfer type to determine if the partner cost should be automatically transferred using stripe
    public partnerCostTransferType: PartnerCostTransferType = PartnerCostTransferType.StripeTransfer;

    constructor(key: string = null, json: any = null) {
        super();
        this.key = key;
        this.createdOn = new Date();
        this.planChangedOn = new Date();
        if(json) {
            this.fromJson(json);
        }
    }

    public fromJson(json: any): void {
        this.title = json.title || "";
        this.text = json.text || "";
        this.languages = json.languages || "";
        this.owners = json.owners || {};
        this.owner = json.owner || ((Object.keys(this.owners).length > 0) ? Object.keys(this.owners)[0] : "");
        this.createdOn = json.createdOn || null;
        this.deletedOn = (json.deletedOn) ? new Date(json.deletedOn) : null;
        this.published = json.published || false;
        this.order = json.order || 9999;
        this.mapStyle = json.mapStyle || 'default';
        this.image.fromJson(json.image);
        // Ensure that project key is properly set on legacy media
        if(!this.image.project) this.image.project = this.key;
        this.allowOfflineMode = json.allowOfflineMode || false;
        this.exclusiveRouteMode = json.exclusiveRouteMode || false;
        this.creatorPlan = json.creatorPlan || PlanType.PERSONAL;
        this.planChangedOn = json.planChangedOn || this.createdOn;
        this.billingType = json.billingType || null;
        if(json.position) this.position = new Coords(json.position.lat, json.position.long, true);
        this.partnerId = json.partnerId || "";
        this.partnerPct = json.partnerCost || 0;
        this.partnerCostTransferType = json.partnerCostTransferType || PartnerCostTransferType.StripeTransfer;
    }

    public updateFromJson(json: any): void {
        console.log("updateFromJson() is deprecatd, use fromJson() to comply with DatabaseModel abstract class");
        return this.fromJson(json);
    }

    public toJson(): any {
        let json: any = {
            title: this.title,
            text: this.text,
            createdOn: this.createdOn,
            deletedOn: this.deletedOn || null,
            published: this.published,
            order: this.order,
            languages: this.languages || "",
            owners: this.owners || {},
            owner: this.owner,
            mapStyle: this.mapStyle,
            allowOfflineMode: this.allowOfflineMode,
            exclusiveRouteMode: this.exclusiveRouteMode,
            creatorPlan: this.creatorPlan || PlanType.PERSONAL,
            planChangedOn: this.planChangedOn,
            billingType: this.billingType,
            image: this.image.toJson(),
            partnerId: this.partnerId,
            partnerCost: this.partnerPct,
            partnerCostTransferType: this.partnerCostTransferType,
        };
        if(this.position) json.position = this.position.toJson();
        return json;
    }

    /**
     * @deprecated
     * Deprecated because the project owner is now held in the property 'owner',
     * and the boolean value in 'owners' map no longer has meaning.
     */
    public addOwner(userKey: string) {
        this.owners[userKey] = true;
    }

    /**
     * @deprecated
     * Deprecated because the project owner is now held in the property 'owner',
     * and cannot be removed, only transferred to another member.
     */
    public removeOwner(userKey: string) {
        this.owners[userKey] = null;
    }

    /**
     * @deprecated
     * Deprecated because the boolean values in 'owners' map no longer has meaning.
     * 'owners' simply holds the member user keys (collaborators + owner).
     */
    public getOwnerKeys(): Array<string> {
        // Filter out elements with the value null / false
        return Object.keys(this.owners).filter(key => this.owners[key]);
    }

    public getLanguageOptions(): LanguageOption[] {
        return this.parseLanguages(this.languages);
    }

    private parseLanguages(languageStr: string): LanguageOption[] {
        if(!languageStr) return [];

        return languageStr.split(',').map(code => {
            let id = code.trim().toLocaleLowerCase();
            return LanguageMap[id] ? LanguageMap[id] : { code: code, label: 'Unknown ' + code, icon: 'language' };
        });
    }

    public toString(): string {
        return `\Project Object Key:'${this.key}' Title:'${this.title}' CreatedOn:${this.createdOn}`;
    }
}
